import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useClientVaultTemplates, ClientVaultFolderTemplate } from '../client-vault/useData';
import { createTemplate, updateTemplate, deleteTemplate } from '../client-vault/mutations';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';
import {
  Button,
  TextInput,
  Textarea,
  Label,
  ColorPicker,
  IconPicker,
  ConfirmationDialog,
  Switch,
  Badge,
} from '../../components/ui';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../components/ui/Modal';

const List: React.FC = () => {
  const { colors } = useTheme();
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ClientVaultFolderTemplate | null>(null);
  const [templateFormData, setTemplateFormData] = useState({
    name: '',
    description: '',
    color: '#3B82F6',
    icon: '',
    displayOrder: 0,
    isActive: true,
  });
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const [showInactive, setShowInactive] = useState(false);

  const { templates, isLoading, mutate } = useClientVaultTemplates(showInactive);
  const { showNotification } = useNotification();

  const openTemplateModal = (template?: ClientVaultFolderTemplate) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateFormData({
        name: template.name,
        description: template.description || '',
        color: template.color || '#3B82F6',
        icon: template.icon || '',
        displayOrder: template.displayOrder,
        isActive: template.isActive,
      });
    } else {
      setEditingTemplate(null);
      setTemplateFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        icon: '',
        displayOrder: 0,
        isActive: true,
      });
    }
    setShowTemplateModal(true);
  };

  const handleCreateTemplate = async () => {
    if (!templateFormData.name) {
      showNotification('Please enter a template name', 'error');
      return;
    }

    try {
      await createTemplate(templateFormData);
      showNotification('Template created successfully', 'success');
      setShowTemplateModal(false);
      setTemplateFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        icon: '',
        displayOrder: 0,
        isActive: true,
      });
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to create template', 'error');
    }
  };

  const handleUpdateTemplate = async () => {
    if (!editingTemplate || !templateFormData.name) {
      return;
    }

    try {
      await updateTemplate(editingTemplate.id, templateFormData);
      showNotification('Template updated successfully', 'success');
      setShowTemplateModal(false);
      setEditingTemplate(null);
      setTemplateFormData({
        name: '',
        description: '',
        color: '#3B82F6',
        icon: '',
        displayOrder: 0,
        isActive: true,
      });
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to update template', 'error');
    }
  };

  const handleDeleteTemplate = async (templateId: number) => {
    try {
      await deleteTemplate(templateId);
      showNotification('Template deleted successfully', 'success');
      setDeleteConfirm(null);
      mutate();
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to delete template', 'error');
    }
  };

  if (isLoading) {
    return (
      <div className={cn('flex flex-col items-center justify-center min-h-[50vh]', colors.background)}>
        <div className={cn('h-10 w-10 animate-spin rounded-full border-2 border-t-transparent', colors.primary)} />
        <p className={cn('mt-4 text-sm', colors.textMuted)}>Loading templates...</p>
      </div>
    );
  }

  return (
    <div className={cn('min-h-screen', colors.background)}>
      <header className={cn('sticky top-0 z-10 border-b', colors.surface, colors.border)}>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between gap-4">
            <div className="flex items-center gap-2 shrink-0">
              <div className={cn('flex h-10 w-10 items-center justify-center rounded-xl', colors.info)}>
                <svg className="h-5 w-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v7a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 16a1 1 0 011-1h4a1 1 0 011 1v3a1 1 0 01-1 1H5a1 1 0 01-1-1v-3zM14 13a1 1 0 011-1h4a1 1 0 011 1v6a1 1 0 01-1 1h-4a1 1 0 01-1-1v-6z" />
                </svg>
              </div>
              <span className={cn('text-lg font-semibold', colors.text)}>Vault Templates</span>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/client-vault"
                className={cn(
                  'flex h-9 w-9 items-center justify-center rounded-lg border transition-colors',
                  colors.border,
                  colors.surfaceHover,
                  'hover:bg-gray-100 dark:hover:bg-gray-700'
                )}
                title="Client Vault"
                aria-label="Client Vault"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </Link>
              <div className={cn('flex items-center gap-2 rounded-lg border px-3 py-2', colors.border, colors.surface)}>
                <Switch
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  id="show-inactive"
                />
                <Label htmlFor="show-inactive" className={cn('cursor-pointer text-sm', colors.text)}>
                  Show inactive
                </Label>
              </div>
              <Button onClick={() => openTemplateModal()} variant="default" size="default" className="h-9 gap-1.5">
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New template
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
        {templates.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className={cn('flex h-20 w-20 items-center justify-center rounded-2xl', colors.info)}>
              <svg className="h-10 w-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <h2 className={cn('mt-6 text-xl font-semibold', colors.text)}>No templates yet</h2>
            <p className={cn('mt-2 max-w-sm text-sm', colors.textMuted)}>Create folder templates to use in client vaults (e.g. Antivirus, Domains, Email).</p>
            <Button onClick={() => openTemplateModal()} variant="default" className="mt-6 gap-2">
              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Create first template
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-5">
            {templates.map((template) => {
              const templateColor = template.color || '#6366f1';
              return (
                <div
                  key={template.id}
                  className={cn(
                    'rounded-xl border overflow-hidden transition-shadow',
                    colors.surface,
                    colors.border,
                    'hover:shadow-md'
                  )}
                >
                  <div
                    className={cn('flex border-l-4', colors.surface)}
                    style={{ borderLeftColor: templateColor }}
                  >
                    <div className="flex flex-1 min-w-0 items-center gap-3 p-4">
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg text-lg"
                        style={{ backgroundColor: `${templateColor}20`, color: templateColor }}
                      >
                        {template.icon || '📁'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className={cn('font-semibold truncate', colors.text)}>{template.name}</h3>
                        {template.description && (
                          <p className={cn('text-xs truncate mt-0.5', colors.textMuted)}>{template.description}</p>
                        )}
                      </div>
                      <div className={cn('flex items-center gap-2 text-xs shrink-0', colors.textMuted)}>
                        <span>Order {template.displayOrder}</span>
                        {!template.isActive && (
                          <Badge variant="warning" className="text-[11px]">
                            Inactive
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-0.5 shrink-0">
                        <button
                          type="button"
                          onClick={() => openTemplateModal(template)}
                          className={cn('p-1.5 rounded-md transition-colors', colors.textMuted, 'hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-indigo-600 dark:hover:text-indigo-400')}
                          title="Edit"
                          aria-label="Edit"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteConfirm(template.id)}
                          className={cn('p-1.5 rounded-md transition-colors', colors.textMuted, 'hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400')}
                          title="Delete"
                          aria-label="Delete"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>

      {/* Template Modal */}
      <Modal isOpen={showTemplateModal} onClose={() => setShowTemplateModal(false)} size="md">
        <ModalHeader>
          <ModalTitle>{editingTemplate ? 'Edit Template' : 'Create Template'}</ModalTitle>
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div>
              <Label htmlFor="template-name" required>Name</Label>
              <TextInput
                id="template-name"
                value={templateFormData.name}
                onChange={(e) => setTemplateFormData({ ...templateFormData, name: e.target.value })}
                placeholder="e.g., Antivirus, Domains, Email"
              />
            </div>
            <div>
              <Label htmlFor="template-description">Description</Label>
              <Textarea
                id="template-description"
                value={templateFormData.description}
                onChange={(e) => setTemplateFormData({ ...templateFormData, description: e.target.value })}
                rows={3}
              />
            </div>
            <div>
              <ColorPicker
                label="Color"
                value={templateFormData.color}
                onChange={(color) => setTemplateFormData({ ...templateFormData, color })}
                presetColors={['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4', '#84CC16']}
              />
            </div>
            <div>
              <IconPicker
                label="Icon"
                value={templateFormData.icon}
                onChange={(icon) => setTemplateFormData({ ...templateFormData, icon })}
                helperText="Select an icon for your template"
              />
            </div>
            <div>
              <Label htmlFor="template-display-order">Display Order</Label>
              <TextInput
                id="template-display-order"
                type="number"
                value={templateFormData.displayOrder.toString()}
                onChange={(e) => setTemplateFormData({ ...templateFormData, displayOrder: parseInt(e.target.value) || 0 })}
                min="0"
              />
            </div>
            <div className={cn('flex items-center gap-2 p-4 rounded-xl border', colors.surfaceHover, colors.border)}>
              <Switch
                checked={templateFormData.isActive}
                onChange={(e) => setTemplateFormData({ ...templateFormData, isActive: e.target.checked })}
                id="template-active"
              />
              <Label htmlFor="template-active" className="cursor-pointer font-medium">Active</Label>
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <Button
            onClick={() => {
              setShowTemplateModal(false);
              setEditingTemplate(null);
              setTemplateFormData({
                name: '',
                description: '',
                color: '#3B82F6',
                icon: '',
                displayOrder: 0,
                isActive: true,
              });
            }}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            onClick={editingTemplate ? handleUpdateTemplate : handleCreateTemplate}
            variant="default"
            className={cn(colors.primary, colors.primaryText, colors.primaryHover)}
          >
            {editingTemplate ? 'Update' : 'Create'}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation */}
      <ConfirmationDialog
        isOpen={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={() => deleteConfirm && handleDeleteTemplate(deleteConfirm)}
        title="Delete Template"
        message={`Are you sure you want to delete this template? This action cannot be undone. If the template is being used by any folders, you'll need to deactivate it instead.`}
      />
    </div>
  );
};

export default List;

